/**
 * ONE TAP — the cel-shading material. (Art direction: SUNLIT CEL, DESIGN.md section 4.)
 *
 * THE ONE IDEA IN THIS FILE: value is authored, not simulated.
 *
 * An overhead surface belongs to a band of greys. A ground plane belongs to a clearly brighter
 * one. A wall belongs to a third, brighter again and very nearly white. Those three bands are
 * written down below in CIE L*, and everything else here exists to guarantee that a surface
 * lands inside the one its orientation says it should — in full sun, in the shadow of a plant
 * room, or on the far side of the arena.
 *
 * The bands have been re-authored for the EXTERIOR (DESIGN.md 4, "The Arena Is a ROOFTOP"). The
 * mechanism is unchanged; what changed is that a cast shadow now lands on the ground plane in
 * front of the player rather than on almost nothing, so the ground band is the one that had to
 * grow, and overhead surfaces — rare on a roof — gave up the range to pay for it.
 *
 * The light does not set the value. It sets a normalised 0..1 "how sunlit is this surface",
 * and that factor moves the surface WITHIN its band. Bands are separated by at least fifteen
 * L* points and interpolation cannot leave one, so the room reads in greyscale with the colour
 * thrown away — which is the actual test — while a cast shadow still reads as a sun event
 * because it is worth most of a band's width.
 *
 * Round two authored one grey per orientation and applied a single global shade->sun
 * MULTIPLIER on top. A multiplier is unbounded, and a review measured the consequence: floor
 * L* 79 against wall L* 81, i.e. the horizontal and vertical planes of the room had merged
 * into one mass and only the ink outline was still describing the architecture. A pair of
 * endpoints per band cannot do that. It is also why the walls are now near-white rather than
 * mid grey: white architecture under a real sky is the entire point of DESIGN.md 4, and a
 * white wall is what makes a team-coloured player, an amber gun and a red stairwell read.
 *
 * OVERHEAD SURFACES GET THEIR OWN PARAMETER. A ceiling faces away from both the sun and the
 * sky, so "how sunlit" is a constant on it, and using it left every soffit in the building on
 * one flat near-black value — a hole in the render rather than a roof. Height, grazing angle
 * toward the open edge and a little bounce place it instead. See CEIL_W_*.
 *
 * Everything else:
 *
 *  - HARD BANDS. A generated TOON_BANDS-step ramp with razor-thin (2 texel) transitions, so
 *    the terminator is crisp. Band edges are placed against the sun's actual direction (see
 *    lighting.ts) so sunward walls, floors and away-facing walls take three distinct steps.
 *  - WARM LIGHT / COOL SHADE, taken from the lights themselves rather than from a bolted-on
 *    tint: the surface is coloured by the hue of whatever is lighting it, normalised to
 *    luminance 1 so hue never changes exposure. Sun is warm, sky and ambient are cool, so the
 *    grade falls out of the rig for free — and a muzzle flash or a team-coloured flare tints
 *    the wall it lands on without any extra plumbing.
 *  - FLAT SPECULAR SHAPES. A Blinn lobe pushed through `smoothstep(t - fwidth, t + fwidth)`
 *    so a highlight is a crisp block, antialiased but never a gradient. Off by default —
 *    it belongs on glass, polished floors and metal, not on everything. Opt in with
 *    {@link setToonSpecular}.
 *  - RIM. A tight fresnel edge that fades out with distance, so it separates a nearby
 *    player from the background without turning every far floor into a grazing-angle wash.
 *
 * There is deliberately NO hatching here. An earlier art direction called for screen-space
 * ink hatching; the sunlit-cel direction removed it as a comic-book tell (DESIGN.md 4).
 *
 * Every toon material shares one GL program (uniform-driven, single `customProgramCacheKey`),
 * so a scene full of them is a handful of state changes.
 *
 * Alpha carries the SELECTIVE BLOOM MASK: `a = 1 - bloom`. Anything that never opts in
 * writes the default 1.0 and is therefore excluded from bloom. See postfx.ts.
 */

import * as THREE from 'three';
import { PALETTE, TOON_BANDS } from '@shared/constants';
import {
  AMBIENT_IRRADIANCE,
  HEMI_IRRADIANCE_GROUND,
  HEMI_IRRADIANCE_SKY,
  KEY_IRRADIANCE,
  SHADOW_INTENSITY,
} from './lighting';
import { GRADE_EXPOSURE, GRADE_SHADOW_LIFT, GRADE_TONE_KNEE } from './postfx';
import {
  attachOutline,
  detachOutline,
  ensureHullNormals,
  setOutlineResolution,
  setOutlineScale,
} from './outline';

export { attachOutline, detachOutline, ensureHullNormals, setOutlineResolution, setOutlineScale };

export interface ToonOpts {
  color: number;
  emissive?: number;
  emissiveIntensity?: number;
  rim?: number;
  /**
   * Legacy flag from the ink-and-neon art direction. Hatching was removed in the sunlit-cel
   * pass (DESIGN.md 4: "No hatching. No halftone. No paper grain."), so this is accepted and
   * ignored. Kept so existing call sites keep compiling.
   */
  hatch?: boolean;
}

// ---------------------------------------------------------------------------
// Tuning (rendering-internal; gameplay numbers all come from @shared/constants)
// ---------------------------------------------------------------------------

/**
 * Darkest band as a fraction of full key irradiance. The key is only part of the light in
 * frame — the sky hemisphere and a small ambient carry the rest — so a shadow-band white
 * wall still lands well clear of black. Lifted, never crushed.
 */
const BAND_FLOOR = 0.2;
/** dotNL at which the terminator step happens. Right on the geometric light/dark boundary. */
const BAND_TERMINATOR = 0.02;
/**
 * dotNL at which the brightest band starts. Placed below every sun-facing orientation the
 * arena contains (with the sun at 34 deg elevation / 22 deg azimuth: open decks 0.56, +X walls
 * 0.77) and above the ones that only glance the sun (+Z walls 0.31). So sun-facing surfaces
 * take the lit band, glancing ones the mid band, and everything turned away the shadow band —
 * three steps from geometry alone, before a single cast shadow is drawn.
 *
 * The margins either side are also what makes this survive the sun DROPPING at HIGH NOON
 * (lighting.ts, SUN_ELEVATION_NOON_DEG): at 28 degrees a deck still reads 0.47 and a +Z wall
 * only 0.33, so nothing crosses a band edge and sudden death lengthens shadows without
 * re-shading the arena.
 */
const BAND_HIGHLIGHT = 0.42;
/**
 * Band value spacing. Linear: three genuinely equal steps (0.20 / 0.60 / 1.00). The old
 * curve pushed the mid band to 0.69, which put it close enough to the lit band that the
 * terminator stopped reading.
 */
const BAND_CURVE = 1.0;
/** Ramp resolution. Higher = more precise step placement; transitions stay 2 texels. */
const RAMP_SIZE = 256;

// ---------------------------------------------------------------------------
// THE VALUE LADDER
// ---------------------------------------------------------------------------

/**
 * THREE BANDS, SIX NUMBERS, IN CIE L*.
 *
 * Each orientation gets a *band* — a low end for "no sun reaches this at all" and a high end
 * for "in full sun" — and a surface can never leave the band its orientation puts it in. That
 * is the whole change from round two, which had one authored grey per orientation and a single
 * global shade->sun multiplier on top: a multiplier is unbounded, so a sunlit floor climbed
 * into the wall band and the ladder stopped being a ladder. Interpolating INSIDE a band cannot
 * do that, by construction.
 *
 * Authored in L* rather than in display fractions because L* is the unit the ladder is judged
 * in: desaturate a screenshot, sample a deck and a parapet, and the numbers below are what the
 * sampler should read. The required separation is 15 L* points between adjacent bands, worst
 * case to worst case — i.e. a SUNLIT deck must still sit 15 points under a SHADED wall — and
 * these are computed (not estimated) through the full grade: exposure, tonemap shoulder,
 * black-point lift and sRGB encode, for PALETTE.base white.
 *
 * THE EXTERIOR LADDER:
 *
 *     soffit 24-34        deck 53-69        wall 85-95.5      sky 62-91
 *            \___ 19 ___/      \___ 16 ___/
 *
 * WHAT MOVED, AND WHY. The pivot to open rooftops (DESIGN.md 4) changed two things about this
 * table and left the third alone.
 *
 *  1. THE DECK BAND WIDENED AND DROPPED (60-72 -> 54-69 -> 53-69). Indoors the sun reached almost
 *     no floor, so a 12-point band was mostly decorative and its bottom end only had to clear a
 *     ceiling. Outdoors a cast shadow lying across an open deck IS the frame's subject, and the
 *     width of this band is literally how strong that shadow is: SIXTEEN points of L* between
 *     "in the sun" and "behind the plant room" on the same slab of concrete. Every point of
 *     that width had to be bought from somewhere, which is what moved the other two.
 *  2. THE SOFFIT BAND DROPPED AND NARROWED (30-42 -> 24-35 -> 24-34). Overhead surfaces used to
 *     be every ceiling in a multi-deck building, i.e. a third of the frame, and had to be lit
 *     enough not to read as holes. On a roof they are the exception the design brief says they
 *     should be — canopy undersides, the belly of a walkway, the inside of a vent hood — so they
 *     can be genuinely dark and buy the deck band the room it needs.
 *  3. THE WALL BAND BARELY MOVED (88-95 -> 85-95.5). White painted concrete in full sun is still
 *     the top of the range, and the half point is the brief's "let sunlit white glow just under
 *     clipping" spent to the last code: at the retuned key colour the top of this band renders
 *     #FDF2DC, i.e. red at 253 of 255. It is a HARD CEILING, not a preference — see lighting.ts
 *     KEY_COLOR. The sky tops out at 91 (a sunlit cloud) and the sun disc lives above everything.
 *
 * PREDICTED MEASUREMENTS, recomputed for the SUNNIER rig (lighting.ts: key 1.45 suns at 0xfff2d0,
 * sky fill 0.43 at 0x9ccdff) and the composite's chroma boost — which, note, was a documented
 * constant that had never actually been wired into the shader until this pass (postfx.ts,
 * GRADE_SATURATION). Computed by pushing the rig's irradiance through the toon ramp, the band
 * interpolation and the entire postfx grade. These are what a sampler on a screenshot should
 * read, for PALETTE.base white — with the sRGB colour beside it, because the hue swing is doing
 * as much work here as the value:
 *
 *     deck in full sun          68.4  #a9a79f     soffit, top of its band     33.5
 *     deck in a cast shadow     52.8  #6e8097     soffit, bottom              23.6
 *     wall facing the sun       95.9  #fdf2dc     wall glancing it            93.7  #f7ecd5
 *     wall turned away          85.6  #dcd5c8     cloud, sunlit               90.2  #e9e3d4
 *
 *     GAP  soffit band top (33.5) -> deck band floor (52.4)   = 18.9   (bar: 15)
 *     GAP  deck band top (69.0)   -> wall band floor (85.6)   = 16.6   (bar: 15)
 *     sun-vs-shadow on one slab of deck                       = 15.5   (was 14.3, 13.7)
 *     hue swing across that same terminator                   = 51 sRGB codes of b-r
 *     darkest pixel the renderer can produce                  = 13.2   (never 0 — DESIGN.md 4)
 *
 * The two GAP figures are quoted BAND EDGE TO BAND EDGE, i.e. the worst case the ladder can
 * produce rather than the case the sun happens to light; measured surface-to-surface the same
 * gaps are 27.4 and 17.2.
 *
 * Under HIGH NOON, with the sun dropped to 28 degrees and the composite's contrast gamma on,
 * the same figures are 64.2 / 47.4 / 95.3 / 83.7, i.e. worst-case gaps of 19.0 and 18.7 and a
 * deck shadow worth 16.7 points. Sudden death HARDENS the ladder; it does not break it.
 *
 * THE POINT OF THE RETUNE, stated as a number: the frame got brighter AND the cast shadow got
 * STRONGER (13.7 -> 14.3 -> 15.5, and 14.6 -> 15.2 -> 16.7 at HIGH NOON) while the sunlit top of
 * the range went 95.0 -> 95.9. The original relight failed because brighter meant flatter, so
 * that inequality is the thing to check first if any of this is ever retuned again.
 *
 * The arena paints its architecture in a range of whites and those land slightly lower — see
 * ALBEDO_ENFORCE, which was tightened precisely because the previous pass measured a shaded
 * floor at L* 48 against an authored 60 and lost the ladder on the drift. With the tightened
 * enforcement the palette's darkest possible concrete lands at 51.6 rather than 53.0, so the
 * soffit gap is still 18.1 at the worst paint the arena can use. Deliberate wayfinding colour —
 * jade, team azure, signal red, ink signage — is NOT pulled up into these bands; colour that
 * means something has to keep its own value.
 */
const L_CEILING_SHADE = 24;
const L_CEILING_SUN = 34;
const L_FLOOR_SHADE = 53;
const L_FLOOR_SUN = 69;
const L_WALL_SHADE = 85;
const L_WALL_SUN = 95.5;

/**
 * Where the value ladder crosses over from "wall" to "floor", in world normal Y.
 *
 * The low edge is well off zero so a raked or battered wall panel does not drift out of the
 * wall band, and the high edge is short of 1.0 so the arena's 45-degree traversal ramps read
 * as floors — which is what they are, and what a player needs them to look like.
 */
const LADDER_KNEE_LO = 0.3;
const LADDER_KNEE_HI = 0.95;

/**
 * The same crossover for OVERHEAD surfaces, and deliberately tighter at the low end.
 *
 * This is the "lit leading edge" of the ceiling brief, and it is geometry, not a highlight: a
 * slab's fascia is vertical, so it is already in the wall band and already near-white — but
 * the moment a soffit is chamfered, coffered or raked, a wide knee starts dragging it toward
 * the ceiling band and the slab loses its edge. Requiring a face to be within ~27 degrees of
 * flat before it counts as a ceiling keeps every rake, every beam side and every chamfer up in
 * the wall band, so a slab reads as a bright-edged SOLID with thickness rather than as a hole
 * with a dark plate over it.
 */
const CEIL_KNEE_LO = 0.45;
const CEIL_KNEE_HI = 0.92;

/**
 * OVERHEAD OPENNESS — the ceiling band's own shade->sun parameter.
 *
 * A ceiling faces down. It never takes the sun, so `lit` is a near-constant on it and using
 * `lit` to place a ceiling inside its band leaves every soffit in the building on one flat
 * value — which is exactly the "the ceiling reads as a HOLE in the render" finding. Overhead
 * surfaces get their own parameter instead, mixed from three signals:
 *
 *  - HEIGHT. On a stacked-roof map the higher a soffit is, the more open sky is bouncing up
 *    into it off the decks below. A vent hood at deck level sits at the bottom of the band; the
 *    underside of a walkway three roofs up sits at the top of it. Free vertical wayfinding:
 *    each level's overhangs are a different value, which is worth a lot in an arena whose
 *    premise is verticality. The range is low and tight now because outdoors these are canopies
 *    and ducting, not storey-high slabs.
 *  - EDGE. A flat plane seen from below is face-on directly overhead and grazing out toward
 *    its far edge — which, on a canopy, is where the daylight is. Fading up toward grazing is
 *    therefore "brighter at the outer edge, falling toward the centre", and it costs one dot
 *    product.
 *  - LIT, at a small weight, so a soffit near enough to a sunlit deck to catch real bounce
 *    still responds to it.
 *
 * The weights sum to 1.0 so the parameter spans the full band.
 */
const CEIL_Y_LO = 2.0;
const CEIL_Y_HI = 26.0;
/** dot(N,V) below which an overhead face counts as fully "at the edge". */
const CEIL_EDGE_WIDTH = 0.55;
const CEIL_W_HEIGHT = 0.62;
const CEIL_W_EDGE = 0.2;
const CEIL_W_LIT = 0.18;

/**
 * Azimuth wrap. Two perpendicular walls that share a toon band would otherwise vanish into
 * each other at the corner.
 *
 * Applied to the surface's POSITION INSIDE its band rather than to its value, which is the
 * only form that cannot break the ladder: a wall facing the sun's azimuth moves up its own
 * band and a wall facing away moves down it, and neither can leave.
 */
const ORIENT_AZIMUTH = 0.16;

/**
 * ALBEDO, AND HOW HARD THE LADDER IS ENFORCED OVER IT.
 *
 * THIS IS WHERE THE LAST REVIEW'S LADDER FAILURE ACTUALLY LIVED, and it is worth being precise
 * about, because the six numbers above were never the problem.
 *
 * A surface's final radiance is `band_value * otEff`, where `otEff` interpolates between "shade
 * strictly in proportion to how dark this was painted" (otEff = albedo ratio) and "land exactly
 * on the band" (otEff = 1). At the old settings a mid-grey concrete — albedo ratio ~0.55
 * against PALETTE.base — got otEff ~0.54, i.e. it rendered at 54% of its band's radiance. In
 * L* that is a NINE POINT drop, and it is one-sided: the paler ceiling paint next to it did not
 * move. Authored floor 60 / ceiling 42 measured floor 48 / ceiling 37, and eighteen points of
 * separation became eleven. The ladder was correct and the enforcement was not delivering it.
 *
 * The fix is to make convergence REACH FULL STRENGTH while a paint is still recognisably
 * architectural, instead of asymptoting toward it somewhere up near white. Three numbers now
 * instead of two, and the two thresholds are placed against the palette's MEASURED luminance
 * ratios rather than by eye (ratio = a paint's linear luminance over PALETTE.base's):
 *
 *     ink 0.03   team azure 0.25   signal red 0.26   empty grey 0.36   jade 0.38
 *     |------------------ untouched, below ENFORCE_FLOOR ------------------|
 *                                                     amber 0.58  (partly pulled)
 *                          |---- fully enforced, above ENFORCE_FULL ----|
 *     darkest concrete 0.66   sun yellow 0.72   base2 0.87   base 1.00
 *
 *   - below ENFORCE_FLOOR (0.40)  nothing is pulled at all. Team colour, ink signage, jade,
 *                                 the empty gun's grey: wayfinding value IS wayfinding meaning
 *                                 (DESIGN.md 4), and none of it is a plane you read a room by.
 *   - above ENFORCE_FULL (0.66)   the pull is at full ALBEDO_ENFORCE. This is exactly where the
 *                                 arena's architectural range starts: PALETTE.base2 taken 55%
 *                                 of the way to PALETTE.shadow, the darkest concrete the
 *                                 palette can make, lands at 0.66.
 *   - between                     a smooth ramp, so nothing pops as a material darkens.
 *
 * At ALBEDO_ENFORCE 0.82 that darkest concrete gets otEff 0.935 — one and a half L* points off
 * its band instead of nine — and everything paler is inside a point. The ladder's 19 and 16
 * point gaps therefore survive the arena's ENTIRE white range, which is the property that has
 * now failed twice.
 *
 * ALBEDO_MIN is the anti-crush clamp underneath all of it: no surface is ever shaded as though
 * it reflected less than 9% of what white does. That is what keeps ink signage on a soffit at
 * a readable slate rather than at true black (DESIGN.md 4).
 */
const ALBEDO_ENFORCE = 0.82;
const ENFORCE_FLOOR = 0.4;
const ENFORCE_FULL = 0.66;
const ALBEDO_MIN = 0.09;

/**
 * How much of the light's own hue reaches the surface. 1.0 would paint a shaded white wall in
 * undiluted sky blue; 0 would throw away the warm-sun/cool-shade grade that is the entire
 * colour story. Raised with the sun: the ladder now pins VALUE hard enough that hue is the only
 * channel left to carry "this face is in sunlight and that one is in sky-shade", and at 0.5 a
 * near-white wall band had almost no warm/cool story left in it.
 *
 * RAISED AGAIN, 0.68 -> 0.78 -> 0.83, and it is the cheapest lever in the renderer because it is
 * provably free. `otHue` below is the incident colour renormalised to luminance exactly 1, so
 * `mix( vec3( 1.0 ), otHue, LIGHT_HUE )` also has luminance exactly 1 for any value of
 * LIGHT_HUE — the ladder cannot move, by construction, no matter how far this is pushed. All it
 * spends is chroma.
 *
 * Measured against the retuned rig (lighting.ts): a sunlit white wall goes from 23 to 27 to 34
 * sRGB codes of red-over-blue, and a deck in a cast shadow from 23 to 31 to 41 codes of
 * blue-over-red. FIFTY-ONE codes of hue swing across a terminator that is only fifteen L* points
 * deep. That ratio — a big hue step over a modest value step — is what a sunny day actually looks
 * like, and it is why the frame can get brighter without getting flatter.
 *
 * WHAT STOPS IT GOING FURTHER is not the ladder but the word "white". At 0.83 a sunlit wall
 * measures 13% HSV chroma and still reads as white concrete in warm light; past about 0.9 it
 * starts reading as painted cream, which is a different building. The other end is the same
 * story in reverse — a shadow at 27% chroma is sky-lit concrete, one at 40% is blue paint.
 */
const LIGHT_HUE = 0.83;

/**
 * Rim is DOWN outdoors. Indoors it separated a player from a wall of the same value; on a roof
 * almost everything a player is seen against is either bright sky or bright concrete, so a pale
 * fresnel edge adds glare rather than separation — the ink outline is what draws the silhouette
 * (DESIGN.md 4), and it does it at constant screen weight regardless of distance.
 */
const DEFAULT_RIM = 0.18;
/**
 * Fresnel window for the rim. Deliberately tight: on hard-edged geometry dot(N,V) is
 * constant across a whole face, so a wide window paints entire box sides instead of drawing
 * an edge.
 */
const RIM_START = 0.8;
const RIM_END = 0.995;
/**
 * A pure fresnel rim also fires along any large flat surface seen at a grazing angle, which
 * turns the far half of a floor into a pale wash and destroys long-range readability. Fading
 * it out with view distance keeps it doing its real job.
 */
const RIM_FADE_NEAR = 30.0;
const RIM_FADE_FAR = 80.0;
const RIM_FADE_AMOUNT = 0.85;

/**
 * FLAT SPECULAR, IN TWO PARTS: a crisp block and a hot glint inside it.
 *
 * DESIGN.md 4 asks for "clean flat specular SHAPES — a crisp highlight block, not a stepped ramp
 * on everything", and the block alone delivered that and nothing else. The playtest note this
 * pass is answering is about JOY, and the missing ingredient on glass, water and metal is
 * SPARKLE: a small, very hot core that survives the tonemap's shoulder and reads as a point of
 * light rather than as a lighter patch of surface. So the block is now the shape and the glint
 * is the light, and they are separate powers with separate gains.
 *
 * BOTH ARE NORMALISED AGAINST THE LIGHT'S HUE RATHER THAN MULTIPLIED BY IT, which is a fix as
 * much as a feature. `directionalLights[0].color` in three is colour TIMES intensity — 4.55 with
 * the retuned key — so the old form tied highlight brightness to a lighting knob, and raising
 * the sun by 7% silently blew every highlight in the arena 7% further past clipping. Dividing
 * the hue out and applying an explicit gain means these two numbers mean what they say,
 * regardless of what lighting.ts does next.
 *
 * The glint is what the composite's HIGHLIGHT BLOOM (postfx.ts) is calibrated against: at the
 * arena's spec settings (0.14 to 0.4) a glint core lands at 0.67 to 1.92 of scene radiance on top
 * of a surface that is at most 1.265, i.e. it clears the 1.30 bloom threshold by a wide margin
 * and a plain sunlit wall only grazes it. That is the whole "sparkle on glass and railings, a
 * whisper of glow on the hottest concrete edge and nothing on the rest" rule, expressed as two
 * numbers that have to agree across two files.
 *
 * THE GLINT GAIN IS UP AGAIN (4.0 -> 4.8) and the block is not. They do different jobs: the block
 * is the SHAPE, and a bigger shape on a white building is just a paler patch of building, while
 * the glint is the POINT OF LIGHT and its whole value is how far past the bloom threshold its
 * few pixels reach. Sparkle is bought in the core, never in the area.
 */
const SPEC_POWER = 60.0;
const SPEC_CUT = 0.55;
const SPEC_GAIN = 2.4;
/** The glint: a much tighter lobe, hotter, and small enough that it never becomes a shape. */
const SPEC_GLINT_POWER = 400.0;
const SPEC_GLINT_CUT = 0.4;
const SPEC_GLINT_GAIN = 4.8;
/** Off by default. Glass / polished terrazzo / brushed aluminium opt in. */
const DEFAULT_SPEC = 0.0;

/** Warm sun-wrap tint on the rim, so silhouettes read as lit rather than glowing. */
const RIM_TINT = 0xfff6e0;

// ---------------------------------------------------------------------------
// Deriving the ladder
//
// The three greys above are display values. The shader needs scene radiance. Everything
// between the two is a known, invertible function that postfx.ts owns and publishes, so we
// invert it here rather than hand-tuning a multiplier and hoping — which is how the previous
// build ended up with a floor and a wall on the same grey.
// ---------------------------------------------------------------------------

/** Relative luminance of a palette colour, in linear light. */
function linearLuminance(hex: number): number {
  const c = new THREE.Color().setHex(hex, THREE.SRGBColorSpace);
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
}

function srgbToLinear(v: number): number {
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

/** Inverse of the composite's `highKeyTonemap`. Linear below the knee, log above it. */
function inverseTonemap(y: number): number {
  if (y <= GRADE_TONE_KNEE) return y;
  const headroom = 1 - GRADE_TONE_KNEE;
  const over = Math.min((y - GRADE_TONE_KNEE) / headroom, 0.999);
  return GRADE_TONE_KNEE - headroom * Math.log(1 - over);
}

/** Luminance of the lifted black point the composite mixes toward. */
const LIFT_LUMINANCE = linearLuminance(PALETTE.shadow);

/** Scene radiance (pre-exposure, linear) that ends up displayed as `display`. */
function radianceForDisplay(display: number): number {
  const lin = srgbToLinear(display);
  const tone = (lin - GRADE_SHADOW_LIFT * LIFT_LUMINANCE) / (1 - GRADE_SHADOW_LIFT);
  return inverseTonemap(Math.max(tone, 0)) / GRADE_EXPOSURE;
}

/** CIE L* -> relative luminance Y. The ladder is authored in L*; the shader wants radiance. */
function luminanceForLstar(lStar: number): number {
  const t = (lStar + 16) / 116;
  return t > 6 / 29 ? t * t * t : (3 * (6 / 29) ** 2 * (t - 4 / 29));
}

function linearToSrgb(v: number): number {
  const c = Math.max(v, 0);
  return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

/**
 * Scene radiance (pre-exposure, linear) that a luminance-1 white surface needs in order to be
 * DISPLAYED at the given CIE L*. This is the one function that ties the six authored numbers
 * to the shader, and it inverts the entire grade — sRGB encode, black-point lift, tonemap
 * shoulder, exposure — so retuning any of those in postfx.ts re-derives the ladder instead of
 * silently breaking it.
 */
function radianceForLstar(lStar: number): number {
  return radianceForDisplay(linearToSrgb(luminanceForLstar(lStar)));
}

/**
 * NORMALISING THE LIGHT. `shade` in the shader is total incident luminance divided by the
 * surface's own albedo luminance, which makes it independent of what colour the surface was
 * painted — a jade panel and a white wall agree about how sunlit they are. These two numbers
 * are the ends of its useful range, computed from the rig's published irradiance rather than
 * measured by eye, so retuning a light intensity cannot silently decalibrate the ladder.
 *
 * MIN is a downward face in a cast shadow: the darkest thing the arena can produce.
 * MAX is a wall square-on to the sun with nothing in the way: the brightest.
 *
 * Worked through for the SUNNIER exterior rig, since these decide how strongly a cast shadow
 * reads: with the key at 1.45 suns, the sky hemisphere at 0.40 and ambient at 0.05, MIN is 0.359
 * and MAX 1.614. A sunlit open deck lands at otLit 0.98 (all but the top of the deck band,
 * L* 68.6); the same deck under a cast shadow lands at 0.04 (L* 54.3). Fourteen points of L*
 * with a sixty-two-code hue swing from warm to cool across a hard terminator — that is the
 * shadow band running off a parapet, and it is the loudest single event in the frame, exactly
 * as DESIGN.md 4 asks.
 *
 * Note which way raising the fill moved things. A bigger sky pole lifts MIN as well as MAX, so
 * a shadowed deck moved DOWN its band slightly (otLit 0.09 -> 0.04) even though it is receiving
 * more light: the normalisation is doing its job, and the shadow got stronger rather than
 * washing out. What the extra sky light actually bought is hue — the shadow is bluer — which is
 * the entire reason for raising it.
 */
const SHADE_MIN =
  KEY_IRRADIANCE * BAND_FLOOR * (1 - SHADOW_INTENSITY) + HEMI_IRRADIANCE_GROUND + AMBIENT_IRRADIANCE;
const SHADE_MAX =
  KEY_IRRADIANCE + (HEMI_IRRADIANCE_SKY + HEMI_IRRADIANCE_GROUND) * 0.5 + AMBIENT_IRRADIANCE;

/**
 * THE BAND ENDPOINTS, in scene radiance. Six numbers in, six numbers out; the shader does
 * nothing but pick a band from the world normal and interpolate between two of them.
 */
const RAD_CEILING_SHADE = radianceForLstar(L_CEILING_SHADE);
const RAD_CEILING_SUN = radianceForLstar(L_CEILING_SUN);
const RAD_FLOOR_SHADE = radianceForLstar(L_FLOOR_SHADE);
const RAD_FLOOR_SUN = radianceForLstar(L_FLOOR_SUN);
const RAD_WALL_SHADE = radianceForLstar(L_WALL_SHADE);
const RAD_WALL_SUN = radianceForLstar(L_WALL_SUN);

/** Albedo the ladder is quoted against: the arena's dominant white architecture. */
const BASE_ALBEDO = linearLuminance(PALETTE.base);

/**
 * How bright the rim is allowed to make an overhead silhouette edge. A soffit's own band tops
 * out at L* 42, so scaling its rim by its own value leaves the edge of a slab as invisible as
 * the middle of it. Pinning the overhead rim to the sunlit-floor radiance instead gives every
 * slab a lit leading edge against whatever is behind it — the second half of "slabs must read
 * as solids with thickness".
 */
const CEIL_RIM_RADIANCE = RAD_FLOOR_SUN;

// ---------------------------------------------------------------------------
// Shared uniforms — one object referenced by every material, so a single write
// updates the whole scene without a traversal.
// ---------------------------------------------------------------------------

const shared = {
  uRimColor: { value: new THREE.Color().setHex(RIM_TINT, THREE.SRGBColorSpace) },
  uPixelRatio: { value: 1 },
  uToonTime: { value: 0 },
  /** Sun azimuth, flattened to the ground plane. Drives the azimuth wrap. */
  uSunFlat: { value: new THREE.Vector3(1, 0, 0) },
  /** View distances (start, end) over which cast shadows fade out. See setToonShadowFade. */
  uShadowFade: { value: new THREE.Vector2(1e6, 2e6) },
};

export function setToonPixelRatio(v: number): void {
  shared.uPixelRatio.value = v > 0 ? v : 1;
}

/**
 * Tell the toon grade where the sun is. Only its azimuth is used — the elevation already
 * reaches the material through the light itself. The renderer wires this to the lighting
 * rig's `sunDirection` so the two can never disagree.
 */
export function setToonSunDirection(dir: THREE.Vector3): void {
  const flat = shared.uSunFlat.value;
  flat.set(dir.x, 0, dir.z);
  if (flat.lengthSq() < 1e-8) flat.set(1, 0, 0);
  else flat.normalize();
}

/**
 * Tell the toon grade where the shadow cascade runs out, so cast shadows can be faded to
 * nothing just before it. Outside a directional light's ortho frustum three reports every
 * fragment as fully lit, which paints a hard straight seam across the floor exactly where
 * shadowing stops — a seam that then sweeps across the arena as the cascade follows you.
 * Fading the shadow out over the last stretch of the cascade replaces that edge with aerial
 * perspective, which is what the eye expects at range anyway.
 *
 * The renderer wires this to the lighting rig's own cascade numbers so the two cannot drift.
 */
export function setToonShadowFade(range: THREE.Vector2): void {
  shared.uShadowFade.value.copy(range);
}

/**
 * No-op. Screen-space hatching was cut with the sunlit-cel art direction; the entry point
 * survives so callers (and the renderer's quality tiers) do not need to special-case it.
 */
export function setToonHatchEnabled(_on: boolean): void {
  /* hatching removed — see DESIGN.md section 4 */
}

export function updateToonTime(seconds: number): void {
  shared.uToonTime.value = seconds;
}

// ---------------------------------------------------------------------------
// Compile-hook plumbing. Several features want to patch the same material, so
// hooks chain instead of clobbering `onBeforeCompile`.
// ---------------------------------------------------------------------------

type CompileHook = (shader: THREE.WebGLProgramParametersWithUniforms, renderer: THREE.WebGLRenderer) => void;

interface HookState {
  hooks: CompileHook[];
  keys: string[];
}

function hookState(m: THREE.Material): HookState {
  const ud = m.userData as Record<string, unknown>;
  let st = ud.__otHooks as HookState | undefined;
  if (st === undefined) {
    st = { hooks: [], keys: [] };
    ud.__otHooks = st;
    const local = st;
    m.onBeforeCompile = (shader, renderer) => {
      for (let i = 0; i < local.hooks.length; i++) local.hooks[i]!(shader, renderer);
    };
    m.customProgramCacheKey = () => local.keys.join('+');
  }
  return st;
}

function addCompileHook(m: THREE.Material, key: string, fn: CompileHook): void {
  const st = hookState(m);
  if (st.keys.indexOf(key) !== -1) return;
  st.keys.push(key);
  st.hooks.push(fn);
  m.needsUpdate = true;
}

/** Append a statement to the end of `main()` in a fragment shader. */
function appendToMain(src: string, statement: string): string {
  const last = src.lastIndexOf('}');
  if (last < 0) return src;
  return `${src.slice(0, last)}\n${statement}\n}${src.slice(last + 1)}`;
}

// ---------------------------------------------------------------------------
// Selective bloom mask
// ---------------------------------------------------------------------------

/**
 * Flag a material as an emissive bloom source. `amount` in [0,1] scales how much of its lit
 * colour reaches the bloom pyramid. Works on ANY three material — call it on VFX sprites,
 * neon strips, the gun's chamber glow, anything that should burn.
 *
 * Implementation: the post chain treats the scene buffer's alpha as `1 - bloomMask`, so
 * anything that never calls this is excluded automatically. That means selective bloom costs
 * zero extra geometry passes and stays perfectly occluded by world geometry.
 */
export function setBloomMask(material: THREE.Material, amount: number): void {
  const ud = material.userData as Record<string, unknown>;
  const existing = ud.__otBloomUniform as { value: number } | undefined;
  if (existing !== undefined) {
    existing.value = amount;
    return;
  }
  const uniform = { value: amount };
  ud.__otBloomUniform = uniform;
  addCompileHook(material, 'bloommask', (shader) => {
    shader.uniforms.uBloomMask = uniform;
    shader.fragmentShader = `uniform float uBloomMask;\n${shader.fragmentShader}`;
    shader.fragmentShader = appendToMain(shader.fragmentShader, '\tgl_FragColor.a = 1.0 - uBloomMask;');
  });
}

export function getBloomMask(material: THREE.Material): number {
  const u = (material.userData as Record<string, unknown>).__otBloomUniform as { value: number } | undefined;
  return u === undefined ? 0 : u.value;
}

// ---------------------------------------------------------------------------
// The banded lighting ramp
// ---------------------------------------------------------------------------

let _rampTexture: THREE.DataTexture | null = null;

function bandRamp(): THREE.DataTexture {
  if (_rampTexture !== null) return _rampTexture;

  const bands = Math.max(2, TOON_BANDS);
  const data = new Uint8Array(RAMP_SIZE);

  // Step positions in dotNL space. The first sits right on the terminator; the rest spread
  // toward the highlight so the lit side keeps most of the tonal range.
  const steps: number[] = [];
  for (let i = 0; i < bands - 1; i++) {
    const t = bands === 2 ? 0 : i / (bands - 2);
    steps.push(BAND_TERMINATOR + (BAND_HIGHLIGHT - BAND_TERMINATOR) * t);
  }

  const values: number[] = [];
  for (let i = 0; i < bands; i++) {
    const t = i / (bands - 1);
    values.push(BAND_FLOOR + (1 - BAND_FLOOR) * Math.pow(t, BAND_CURVE));
  }

  // 2-texel transition: enough for the sampler to soften the step without turning it into a
  // gradient, which keeps curved surfaces from crawling with aliasing.
  const soften = 2 / RAMP_SIZE;

  for (let x = 0; x < RAMP_SIZE; x++) {
    const coord = (x + 0.5) / RAMP_SIZE;
    const dotNL = coord * 2 - 1;
    let v = values[0]!;
    for (let s = 0; s < steps.length; s++) {
      const edge = steps[s]!;
      const lo = edge - soften;
      const hi = edge + soften;
      let k: number;
      if (dotNL <= lo) k = 0;
      else if (dotNL >= hi) k = 1;
      else {
        const u = (dotNL - lo) / (hi - lo);
        k = u * u * (3 - 2 * u);
      }
      v = v + (values[s + 1]! - v) * k;
    }
    data[x] = Math.round(Math.min(1, Math.max(0, v)) * 255);
  }

  const tex = new THREE.DataTexture(data, RAMP_SIZE, 1, THREE.RedFormat, THREE.UnsignedByteType);
  tex.name = 'OneTapToonRamp';
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  _rampTexture = tex;
  return tex;
}

// ---------------------------------------------------------------------------
// Fragment injection
// ---------------------------------------------------------------------------

const TOON_HEADER = /* glsl */ `
uniform float uRim;
uniform float uSpec;
uniform vec3 uRimColor;
uniform float uPixelRatio;
uniform float uToonTime;
uniform vec3 uSunFlat;
uniform vec2 uShadowFade;

const vec3 OT_LUMA = vec3( 0.2126, 0.7152, 0.0722 );
`;

/**
 * CASCADE FADE. `shadowIntensity` is three's own "how much light this shadow removes" knob,
 * so scaling it by a distance falloff fades a cast shadow out without touching anything else
 * about the light — at 0 the surface is simply lit, which is precisely the state the geometry
 * beyond the cascade is already in. That makes the boundary continuous instead of a seam.
 *
 * It has to be spliced into `lights_fragment_begin` rather than matched in the material's own
 * source, because `onBeforeCompile` hands us the shader with its `#include`s still unresolved
 * — the text we need does not exist yet at that point. So we expand the chunk ourselves,
 * patch it, and substitute it for the include directive. Scoped to our own materials; three's
 * shared ShaderChunk table is never mutated.
 *
 * `vViewPosition` is in scope here: the stock chunk's very first line uses it.
 */
const SHADOW_INTENSITY_EXPR = /directionalLightShadow\.shadowIntensity/g;
const SHADOW_FADE_GLSL =
  '( directionalLightShadow.shadowIntensity * ( 1.0 - smoothstep( uShadowFade.x, uShadowFade.y, length( vViewPosition ) ) ) )';

function lightsChunkWithShadowFade(): string {
  const chunk = THREE.ShaderChunk.lights_fragment_begin;
  return chunk.replace(SHADOW_INTENSITY_EXPR, SHADOW_FADE_GLSL);
}

const TOON_OUTGOING = /* glsl */ `
	vec3 otBaseLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse;
	vec3 otView = normalize( vViewPosition );
	float otNdV = clamp( dot( normal, otView ), 0.0, 1.0 );

	// World normal, recovered by multiplying from the LEFT by viewMatrix: its rotation block is
	// orthonormal, so the left-multiply IS its inverse. Cheaper and more robust than carrying
	// a second varying through every vertex.
	vec3 otWorldNormal = normalize( ( vec4( normal, 0.0 ) * viewMatrix ).xyz );
	// Same trick for the position: vViewPosition is -mvPosition.xyz, so rotating it back and
	// adding the camera gives world space with no extra varying and no inverse matrix upload.
	vec3 otWorldPos = cameraPosition + ( vec4( - vViewPosition, 0.0 ) * viewMatrix ).xyz;

	// --- WHICH BAND. Well off horizontal at the low end so a raked wall stays a wall, short of
	//     vertical at the high end so a traversal ramp reads as a floor, and tighter overhead so
	//     a chamfer or a beam side stays in the bright wall band and gives a slab its edge.
	float otFloorK = smoothstep( ${LADDER_KNEE_LO.toFixed(3)}, ${LADDER_KNEE_HI.toFixed(3)}, otWorldNormal.y );
	float otCeilK = smoothstep( ${CEIL_KNEE_LO.toFixed(3)}, ${CEIL_KNEE_HI.toFixed(3)}, - otWorldNormal.y );

	// --- HOW SUNLIT IS THIS PIXEL, in [0,1]. Dividing by the surface's own albedo luminance
	//     makes the answer independent of what colour it was painted, and the range constants
	//     come straight from the lighting rig's published irradiance, so the two cannot drift.
	//     A cast shadow lands here — and nowhere else — which is what keeps a shadow a SHAPE
	//     instead of a change of material.
	float otAlbedoLum = max( dot( diffuseColor.rgb, OT_LUMA ), 1e-4 );
	float otLum = max( dot( otBaseLight, OT_LUMA ), 1e-5 );
	float otShade = otLum / otAlbedoLum;
	float otLit = clamp(
		( otShade - ${SHADE_MIN.toFixed(5)} ) / ${(SHADE_MAX - SHADE_MIN).toFixed(5)},
		0.0,
		1.0
	);

	// --- OVERHEAD OPENNESS. A ceiling never takes the sun, so otLit is a constant on it and
	//     cannot place it inside its band; height above the sump, grazing angle toward the open
	//     edge of the slab, and a little bounce do it instead. This is what stops a roof reading
	//     as a hole: the value now MOVES across a soffit and between storeys.
	float otHeight = smoothstep( ${CEIL_Y_LO.toFixed(1)}, ${CEIL_Y_HI.toFixed(1)}, otWorldPos.y );
	float otEdge = 1.0 - smoothstep( 0.0, ${CEIL_EDGE_WIDTH.toFixed(3)}, otNdV );
	float otOpen = clamp(
		${CEIL_W_HEIGHT.toFixed(3)} * otHeight
		+ ${CEIL_W_EDGE.toFixed(3)} * otEdge
		+ ${CEIL_W_LIT.toFixed(3)} * otLit,
		0.0,
		1.0
	);

	// --- POSITION INSIDE THE BAND, then the band itself. The azimuth wrap moves a surface along
	//     its own band and can never push it out of one, which is the property the previous
	//     multiplier form did not have.
	float otT = clamp(
		mix( otLit, otOpen, otCeilK ) + ${ORIENT_AZIMUTH.toFixed(3)} * dot( otWorldNormal, uSunFlat ),
		0.0,
		1.0
	);
	float otBandLo = mix(
		mix( ${RAD_WALL_SHADE.toFixed(5)}, ${RAD_FLOOR_SHADE.toFixed(5)}, otFloorK ),
		${RAD_CEILING_SHADE.toFixed(5)},
		otCeilK
	);
	float otBandHi = mix(
		mix( ${RAD_WALL_SUN.toFixed(5)}, ${RAD_FLOOR_SUN.toFixed(5)}, otFloorK ),
		${RAD_CEILING_SUN.toFixed(5)},
		otCeilK
	);
	float otValue = mix( otBandLo, otBandHi, otT );

	// --- WARM LIGHT / COOL SHADE, taken from the lights themselves. Normalising the incident
	//     colour to luminance 1 strips its brightness and keeps only its hue, so the sun tints
	//     warm, the sky and ambient tint cool, and a team-coloured flare tints its own colour —
	//     without any of them being able to move a surface off its rung of the ladder.
	vec3 otHue = mix( vec3( 1.0 ), otBaseLight / otLum, ${LIGHT_HUE.toFixed(3)} );

	// --- ALBEDO. Dividing by the surface's own luminance and multiplying by the band's target
	//     is what makes the ladder a ladder rather than a hope: PALETTE.base lands ON the band.
	//     Everything else lands proportionally lower, pulled toward the band only as far as it
	//     was already pale (architecture converges, wayfinding colour keeps its value), and
	//     never treated as darker than ALBEDO_MIN so nothing crushes.
	float otRatio = max( otAlbedoLum / ${BASE_ALBEDO.toFixed(5)}, ${ALBEDO_MIN.toFixed(3)} );
	float otEff = otRatio + ( 1.0 - otRatio ) * ${ALBEDO_ENFORCE.toFixed(3)}
		* smoothstep( ${ENFORCE_FLOOR.toFixed(3)}, ${ENFORCE_FULL.toFixed(3)}, otRatio );
	vec3 otShaded = diffuseColor.rgb * otHue * ( otValue * otEff / otAlbedoLum );

	// --- flat specular: a crisp highlight SHAPE, plus a hot GLINT core inside it. Antialiased
	//     but never faded — a gradient here is the stepped-ramp look DESIGN.md 4 rules out.
	//
	//     The light's colour is divided by its own luminance so only its hue survives; the two
	//     gains below then set the actual brightness. Without that, highlight intensity is a
	//     hostage of KEY_INTENSITY. See SPEC_POWER.
	vec3 otSpecular = vec3( 0.0 );
	#if NUM_DIR_LIGHTS > 0
		if ( uSpec > 0.0 ) {
			vec3 otL = directionalLights[ 0 ].direction;
			vec3 otH = normalize( otL + otView );
			float otNdH = clamp( dot( normal, otH ), 0.0, 1.0 );
			vec3 otSunHue = directionalLights[ 0 ].color
				/ max( dot( directionalLights[ 0 ].color, OT_LUMA ), 1e-4 );

			float otLobe = pow( otNdH, ${SPEC_POWER.toFixed(1)} );
			float otLobeW = max( fwidth( otLobe ), 1e-4 );
			float otHi = smoothstep( ${SPEC_CUT.toFixed(3)} - otLobeW, ${SPEC_CUT.toFixed(3)} + otLobeW, otLobe );

			float otGlint = pow( otNdH, ${SPEC_GLINT_POWER.toFixed(1)} );
			float otGlintW = max( fwidth( otGlint ), 1e-4 );
			float otSpark = smoothstep( ${SPEC_GLINT_CUT.toFixed(3)} - otGlintW, ${SPEC_GLINT_CUT.toFixed(3)} + otGlintW, otGlint );

			otSpecular = otSunHue * uSpec * smoothstep( 0.2, 0.7, otLit )
				* ( otHi * ${SPEC_GAIN.toFixed(2)} + otSpark * ${SPEC_GLINT_GAIN.toFixed(2)} );
		}
	#endif

	// --- rim: a tight fresnel edge, faded with distance so a receding floor does not wash out.
	//
	//     Its AMOUNT is the surface's remaining headroom inside its own band, not a free
	//     addition of light. That distinction matters: a big flat floor is seen at a grazing
	//     angle over most of its area, so an unbounded rim quietly lifts the far half of every
	//     floor out of the floor band — which is one of the two ways the previous build lost
	//     the ladder. Bounded this way the rim can shape an edge and can never break a rung.
	//
	//     OVERHEAD IS THE ONE DELIBERATE EXCEPTION. A soffit is dark by design and has almost
	//     no headroom, so its rim is pinned to the sunlit-floor radiance instead: that is the
	//     lit leading edge, and without a bright line along the edge of a slab, a dark plate
	//     and an actual hole in the roof look exactly the same.
	float otRimFade = 1.0 - ${RIM_FADE_AMOUNT.toFixed(2)} * smoothstep( ${RIM_FADE_NEAR.toFixed(1)}, ${RIM_FADE_FAR.toFixed(1)}, length( vViewPosition ) );
	float otRimK = smoothstep( ${RIM_START.toFixed(3)}, ${RIM_END.toFixed(3)}, 1.0 - otNdV ) * uRim * otRimFade;
	float otRim = otRimK * mix( max( otBandHi - otValue, 0.0 ), ${CEIL_RIM_RADIANCE.toFixed(5)}, otCeilK );

	vec3 outgoingLight = otShaded + otSpecular + uRimColor * otRim + totalEmissiveRadiance;
`;

const TOON_OUTGOING_ORIGINAL =
  'vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + totalEmissiveRadiance;';

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * The house material. Everything in the arena, on the players and on the guns should be made
 * with this so the whole frame shares one lighting response.
 */
export function makeToonMaterial(o: ToonOpts): THREE.Material {
  const emissiveHex = o.emissive ?? 0x000000;
  const emissiveIntensity = o.emissiveIntensity ?? (o.emissive !== undefined ? 1 : 0);

  const mat = new THREE.MeshToonMaterial({
    color: new THREE.Color().setHex(o.color, THREE.SRGBColorSpace),
    emissive: new THREE.Color().setHex(emissiveHex, THREE.SRGBColorSpace),
    emissiveIntensity,
    gradientMap: bandRamp(),
    dithering: true,
  });
  mat.name = 'OneTapToon';

  const uRim = { value: o.rim ?? DEFAULT_RIM };
  const uSpec = { value: DEFAULT_SPEC };

  const ud = mat.userData as Record<string, unknown>;
  ud.uRim = uRim;
  ud.uSpec = uSpec;

  addCompileHook(mat, 'onetap-toon', (shader) => {
    shader.uniforms.uRim = uRim;
    shader.uniforms.uSpec = uSpec;
    shader.uniforms.uRimColor = shared.uRimColor;
    shader.uniforms.uPixelRatio = shared.uPixelRatio;
    shader.uniforms.uToonTime = shared.uToonTime;
    shader.uniforms.uSunFlat = shared.uSunFlat;
    shader.uniforms.uShadowFade = shared.uShadowFade;

    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      `#include <common>\n${TOON_HEADER}`,
    );
    shader.fragmentShader = shader.fragmentShader.replace(TOON_OUTGOING_ORIGINAL, TOON_OUTGOING);
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <lights_fragment_begin>',
      lightsChunkWithShadowFade(),
    );
  });

  // Emissives are the only bloom sources in the game besides the sun (DESIGN.md 4).
  if (emissiveIntensity > 0 && emissiveHex !== 0x000000) {
    setBloomMask(mat, Math.min(1, emissiveIntensity));
  }

  return mat;
}

/** Retune a material made by {@link makeToonMaterial} without recompiling it. */
export function setToonRim(material: THREE.Material, rim: number): void {
  const u = (material.userData as Record<string, unknown>).uRim as { value: number } | undefined;
  if (u !== undefined) u.value = rim;
}

/**
 * Give a material a crisp flat highlight. Use it on glass curtain walls, polished terrazzo,
 * brushed aluminium and the gun — not on painted concrete.
 */
export function setToonSpecular(material: THREE.Material, spec: number): void {
  const u = (material.userData as Record<string, unknown>).uSpec as { value: number } | undefined;
  if (u !== undefined) u.value = spec;
}

/** Accepted and ignored — hatching was removed with the sunlit-cel art direction. */
export function setToonHatch(_material: THREE.Material, _hatch: number): void {
  /* no-op */
}

/** Frees the shared ramp texture. Only the Renderer's dispose() should call this. */
export function disposeToonShared(): void {
  if (_rampTexture !== null) {
    _rampTexture.dispose();
    _rampTexture = null;
  }
}
